import React, { useCallback, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BaseDialog } from 'src/components/BaseDialog/BaseDialog';
import { ts } from 'src/utils/translate';
import { theme } from 'src/theme/mui-theme';
import { getFrontendURL } from 'src/utils/url';
import {
    cancelFrameDrop,
    executeConnectFrames,
    executeMergeFrames,
    PendingFrameDrop,
} from 'src/pages/PlannerPage/Planner/dialogs/FrameActionsDialog/frameActionsActions';

interface FrameActionsDialogProps {
    frameDrop: PendingFrameDrop;
    onClose: () => void;
}

enum FrameActionKey {
    Merge = 'merge',
    Connect = 'connect',
}

interface FrameActionOption {
    key: FrameActionKey;
    title: string;
    image: string;
    description: string;
    run: (frameDrop: PendingFrameDrop) => Promise<void>;
}

export function FrameActionsDialog({ frameDrop, onClose }: FrameActionsDialogProps) {
    const [isProcessing, setIsProcessing] = useState(false);

    const handleCancel = useCallback(() => {
        cancelFrameDrop(frameDrop);
        onClose();
    }, [frameDrop, onClose]);

    const runAction = useCallback(
        (run: (frameDrop: PendingFrameDrop) => Promise<void>) => {
            setIsProcessing(true);
            run(frameDrop)
                .catch(() => cancelFrameDrop(frameDrop))
                .finally(() => onClose());
        },
        [frameDrop, onClose]
    );

    const options: FrameActionOption[] = [
        {
            key: FrameActionKey.Merge,
            title: ts('frame_actions_merge_title', 'Merge frames'),
            image: `${getFrontendURL()}/images/planner_tools/merge_ok.png`,
            description: ts(
                'frame_actions_merge_description',
                'Merge the frames to create a single frame that contains every device from the frames.'
            ),
            run: executeMergeFrames,
        },
        {
            key: FrameActionKey.Connect,
            title: ts('frame_actions_connect_title', 'Connect frames'),
            image: `${getFrontendURL()}/images/planner_tools/connect_ok.png`,
            description: ts(
                'frame_actions_connect_description',
                'Connect the frames to keep both of them, but they will be moved together, and you can set different mounting heights for them.'
            ),
            run: executeConnectFrames,
        },
    ];

    return (
        <BaseDialog
            onClose={handleCancel}
            disableClose={isProcessing}
            title={ts('frame_actions_title', 'Frame actions')}
            subtitle={ts('frame_actions_subtitle', 'What would you like to do?')}
            maxWidth="sm"
            fullWidth
            actions={
                <Button variant="outlined" onClick={handleCancel} disabled={isProcessing}>
                    {ts('frame_actions_cancel', 'Cancel')}
                </Button>
            }
        >
            <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
                {options.map((option) => (
                    <Stack
                        key={option.key}
                        spacing={1}
                        alignItems="center"
                        onClick={() => !isProcessing && runAction(option.run)}
                        sx={{
                            flex: 1,
                            p: 2,
                            borderRadius: 1,
                            border: '2px solid transparent',
                            cursor: isProcessing ? 'default' : 'pointer',
                            opacity: isProcessing ? 0.6 : 1,
                            ':hover': isProcessing
                                ? undefined
                                : {
                                      borderColor: theme.palette.primary.main,
                                      backgroundColor: alpha(theme.palette.primary.main, 0.06),
                                  },
                        }}
                    >
                        <Typography variant="h6" textAlign="center">
                            {option.title}
                        </Typography>
                        <Box
                            component="img"
                            src={option.image}
                            alt={option.title}
                            sx={{ width: '90%', maxWidth: 160, objectFit: 'contain' }}
                        />
                        <Typography variant="body2" textAlign="justify">
                            {option.description}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </BaseDialog>
    );
}
